import { collection, query, getDocs, where, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from './config';
import type { Bike } from '@shared/types/bike';
import { BikeStatus } from '@shared/types/bike';

/**
 * Convert Firestore timestamp to Date
 */
function timestampToDate(timestamp: Timestamp | Date | null | undefined): Date {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  return timestamp.toDate();
}

/**
 * Get bikes by location ID
 */
export async function getBikesByLocation(locationId: string): Promise<Bike[]> {
  const q = query(
    collection(db, 'bikes'),
    where('locationId', '==', locationId),
    where('status', '==', BikeStatus.AVAILABLE)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name,
      description: data.description,
      type: data.type,
      status: data.status,
      locationId: data.locationId,
      halfDayRate: data.halfDayRate || 0,
      fullDayRate: data.fullDayRate || 0,
      twoDayDiscount: data.twoDayDiscount,
      threeDayDiscount: data.threeDayDiscount,
      weekDiscount: data.weekDiscount,
      batteryLevel: data.batteryLevel,
      mileage: data.mileage,
      imageUrls: data.imageUrls || [],
      specifications: data.specifications,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    } as Bike;
  });
}

/**
 * Get all bikes
 */
export async function getBikes(): Promise<Bike[]> {
  const querySnapshot = await getDocs(collection(db, 'bikes'));

  return querySnapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name,
      description: data.description,
      type: data.type,
      status: data.status,
      locationId: data.locationId,
      halfDayRate: data.halfDayRate || 0,
      fullDayRate: data.fullDayRate || 0,
      twoDayDiscount: data.twoDayDiscount,
      threeDayDiscount: data.threeDayDiscount,
      weekDiscount: data.weekDiscount,
      batteryLevel: data.batteryLevel,
      mileage: data.mileage,
      imageUrls: data.imageUrls || [],
      specifications: data.specifications,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    } as Bike;
  });
}

/**
 * Get bike by ID
 */
export async function getBike(bikeId: string): Promise<Bike | null> {
  const docRef = doc(db, 'bikes', bikeId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    name: data.name,
    description: data.description,
    type: data.type,
    status: data.status,
    locationId: data.locationId,
    halfDayRate: data.halfDayRate || 0,
    fullDayRate: data.fullDayRate || 0,
    twoDayDiscount: data.twoDayDiscount,
    threeDayDiscount: data.threeDayDiscount,
    weekDiscount: data.weekDiscount,
    batteryLevel: data.batteryLevel,
    mileage: data.mileage,
    imageUrls: data.imageUrls || [],
    specifications: data.specifications,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  } as Bike;
}

